import { INicu } from "../types/interfaces/INicu";
import { SearchParms } from "../types/classes/SearchParms";
import { Box, List, ListItem, Typography } from "@mui/material";
import NicuSummary from "./NicuSummary";
import { NicuPagination } from "./NicuPagination";
import { DisplayParms } from "../types/classes/DisplayParms";
import { useMemo } from "react";

const NumberOfNicusPerPage: number = 5;

//This function gets a filtered nicu list from a nicu list and filter criteria.
function GetNicusWithFilters(nicus: Array<INicu>, searchParms: SearchParms): Array<INicu> {
    let filteredNicus: Array<INicu> = nicus;

    if(nicus.length > 0 && (searchParms.Name.trim() != "" || searchParms.City != ""
      || searchParms.State != "" || searchParms.ZipCode != "" || searchParms.Level != ""
      || searchParms.LevelAssignedby != "" || searchParms.NumberofBeds != ""))
    {
        if(searchParms.Name.trim() != "") {
            filteredNicus = filteredNicus.filter(nicu => nicu.Name?.toLowerCase().includes(searchParms.Name.trim().toLowerCase()));
        }
        if(searchParms.City != "" )
        {
            filteredNicus = filteredNicus.filter(nicu => nicu.City?.toLowerCase() == searchParms.City.toLowerCase());
        }
        if(searchParms.State != "" )
        {
            filteredNicus = filteredNicus.filter(nicu => nicu.State?.toLowerCase() == searchParms.State.toLowerCase());
        }
        if(searchParms.ZipCode != "" )
        {
            filteredNicus = filteredNicus.filter(nicu => nicu.Zip?.includes(searchParms.ZipCode));
        }
        if(searchParms.Level != "" )
        {
            filteredNicus = filteredNicus.filter(nicu => nicu.LevelId == searchParms.Level);
        }
        if(searchParms.LevelAssignedby != "" )
        {
            filteredNicus = filteredNicus.filter(nicu => nicu.LevelAssignerName?.toLowerCase() == searchParms.LevelAssignedby.toLowerCase());
        }
        if(searchParms.NumberofBeds != "" )
        {
            switch(searchParms.NumberofBeds) {
                case "Less Than 20":
                    filteredNicus = filteredNicus.filter(nicu => nicu.NumberOfBeds == null || nicu.NumberOfBeds == "" || Number(nicu.NumberOfBeds) < 20);
                    break;
                case "20 to 29":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 20 && Number(nicu.NumberOfBeds) <= 29);
                    break;
                case "30 to 39":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 30 && Number(nicu.NumberOfBeds) <= 39);
                    break;
                case "40 to 49":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 40 && Number(nicu.NumberOfBeds) <= 49);
                    break;
                case "50 to 59":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 50 && Number(nicu.NumberOfBeds) <= 59);
                    break;
                case "60 to 69":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 60 && Number(nicu.NumberOfBeds) <= 69);
                    break;
                case "70 to 79":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 70 && Number(nicu.NumberOfBeds) <= 79);
                    break;
                case "80 or More":
                    filteredNicus = filteredNicus.filter(nicu => Number(nicu.NumberOfBeds) >= 80);
                    break;
                default:
                    break;
            }
        }
    }
    
    return filteredNicus;
}

//Get nicus by page number.
function GetNicusByPage (nicus: Array<INicu>, page: number, count: number): Array<INicu> {
    let nicusByPage: Array<INicu> = nicus;
    let startIndex: number = page * count - count;

    if(nicus != null && nicus.length > 0 && startIndex < nicus.length)
    {
        let endIndex: number = startIndex + count;
        nicusByPage = endIndex >= nicus.length ? nicusByPage.slice(startIndex) : nicusByPage.slice(startIndex, endIndex);
    }

    return nicusByPage;
}

//This function creates a list of nicu summaries.
export default function NicuDisplay(nicuList: {nicus: Array<INicu>, searchParms: SearchParms, displayParms: DisplayParms, onDisplayChange: (displayParms: DisplayParms) => void}) {
    if(nicuList.nicus == null || nicuList.nicus.length <= 0) return (<></>);

    const filterNicus: Array<INicu> = useMemo(() => GetNicusWithFilters(nicuList.nicus, nicuList.searchParms), [nicuList.searchParms]);

    filterNicus.sort((object1: INicu, object2: INicu) => {
        const name1 = object1.Name == null ? "" : object1.Name;
        const name2 = object2.Name == null ? "" : object2.Name;
        if(name1 < name2) return -1;
        else return 1;
    });
    
    let pageCount: number = Math.ceil(filterNicus.length / NumberOfNicusPerPage);
    const filterNicusPerPage = useMemo(() => GetNicusByPage(filterNicus, nicuList.displayParms.Page, NumberOfNicusPerPage), [filterNicus, nicuList.displayParms.Page]);

    return (
        <Box paddingLeft={3}>
            <Box sx={{flexGrow: 1}}>
                <List>
                    <ListItem divider={true}>
                        <Box sx={{minWidth: 400}}><Typography variant="h5">Name</Typography></Box>
                        <Typography variant="h5">Location</Typography>
                    </ListItem>
                    {filterNicusPerPage.map((item, index) => (<NicuSummary nicu={item} index={index} key={`nicu-summary-${index}`} displayParms={nicuList.displayParms} onDisplayChange={nicuList.onDisplayChange} />) )}
                </List>
            </Box>
            <Box paddingTop={3}>
                <NicuPagination pageCount={pageCount} displayParms={nicuList.displayParms} onPageChange={nicuList.onDisplayChange} />
            </Box>
        </Box>
    );
}